import { ENV } from '../../config/runtimeEnv';
import {
	Staging,
	StagingCategorySummary,
	StagingSubcategorySummary,
	GeneListItem,
	BulkImportResult
} from '../../types/geneCodes/StagingTypes';
import { http } from '../../lib/http/client';

const API_BASE_URL = ENV.VITE_LABIQ_SERVER || '';

// Helper for common API headers

/**
 * Fetch staging records with filters
 */
export const fetchStagingRecords = async (
	page: number = 1,
	perPage: number = 30,
	sortBy: string = 'idstaging',
	sortOrder: string = 'desc',
	filters: {
		category?: string;
		subcategory?: string;
		gene?: string;
		rs?: string;
		quality?: number;
		client?: string;
		search?: string;
	} = {},
	includeHidden?: boolean
): Promise<{
	data: Staging[];
	total: number;
}> => {
	// Remove empty string filters
	const cleanedFilters = Object.fromEntries(
		Object.entries(filters).filter(([_, value]) => value !== "")
	);

	const response = await http.get(`${API_BASE_URL}/staging`, {
		params: {
			page,
			per_page: perPage,
			sort_by: sortBy,
			sort_order: sortOrder,
			...cleanedFilters,
			// Builder-only and honoured for staff alone; omitted, not false.
			...(includeHidden ? { include_hidden: 1 } : {})
		}
	});

	return {
		data: response.data.data,
		total: response.data.total
	}
};

/**
 * Fetch a single staging record by ID
 */
export const fetchStagingRecord = async (id: number): Promise<Staging> => {
	const response = await http.get(`${API_BASE_URL}/staging/${id}`);

	return response.data.data;
};

/**
 * Create a new staging record
 */
export const createStagingRecord = async (staging: Partial<Staging>): Promise<Staging> => {
	const response = await http.post(
		`${API_BASE_URL}/staging`,
		staging
	);

	return response.data.data;
};

/**
 * Update an existing staging record
 */
export const updateStagingRecord = async (id: number, staging: Partial<Staging>): Promise<Staging> => {
	const response = await http.put(
		`${API_BASE_URL}/staging/${id}`,
		staging
	);

	return response.data.data;
};

/**
 * Delete a staging record
 */
export const deleteStagingRecord = async (id: number): Promise<void> => {
	await http.delete(`${API_BASE_URL}/staging/${id}`);
};

/**
 * Get staging records by category
 */
export const getStagingByCategory = async (
	category: string,
	page: number = 1,
	perPage: number = 25,
	sortBy: string = 'rank',
	sortOrder: string = 'asc',
	quality?: number,
	subcategory?: string
): Promise<{
	data: Staging[];
	total: number;
}> => {

	const response = await http.get(`${API_BASE_URL}/staging/category/${category}`, {
		params: {
			page,
			per_page: perPage,
			sort_by: sortBy,
			sort_order: sortOrder,
			quality,
			subcategory: subcategory === "" ? undefined : subcategory
		}
	});

	return {
		data: response.data.data,
		total: response.data.total
	};
};

/**
 * Get staging records by subcategory within a category
 */
export const getStagingBySubcategory = async (
	category: string,
	subcategory: string,
	page: number = 1,
	perPage: number = 25,
	sortBy: string = 'rank',
	sortOrder: string = 'asc',
	quality?: number
): Promise<{
	data: Staging[];
	total: number;
}> => {
	const response = await http.get(`${API_BASE_URL}/staging/category/${category}/subcategory/${subcategory}`, {
		params: {
			page,
			per_page: perPage,
			sort_by: sortBy,
			sort_order: sortOrder,
			quality
		}
	});

	return {
		data: response.data.data,
		total: response.data.total
	};
};

/**
 * Get staging records by gene
 */
export const getStagingByGene = async (
	gene: string,
	page: number = 1,
	perPage: number = 25,
	sortBy: string = 'idstaging',
	sortOrder: string = 'asc',
	quality?: number,
	category?: string
): Promise<{
	data: Staging[];
	total: number;
}> => {
	const response = await http.get(`${API_BASE_URL}/staging/gene/${gene}`, {
		params: {
			page,
			per_page: perPage,
			sort_by: sortBy,
			sort_order: sortOrder,
			quality,
			category
		}
	});

	return {
		data: response.data.data,
		total: response.data.total
	};
};

/**
 * Search for staging records
 */
export const searchStaging = async (
	search: string,
	page: number = 1,
	perPage: number = 25,
	sortBy: string = 'idstaging',
	sortOrder: string = 'asc',
	quality?: number,
	category?: string
): Promise<{
	data: Staging[];
	total: number;
}> => {
	const response = await http.post(`${API_BASE_URL}/staging/search`,
		{ search },
		{
			params: {
				page,
				per_page: perPage,
				sort_by: sortBy,
				sort_order: sortOrder,
				quality,
				category
			}
		}
	);

	return {
		data: response.data.data,
		total: response.data.total
	};
};

/**
 * Get summary of SNP counts by category
 */
export const getCategorySummary = async (minQuality: number = 1): Promise<StagingCategorySummary[]> => {
	const response = await http.get(`${API_BASE_URL}/staging/summary/categories`, {
		params: { min_quality: minQuality }
	});

	return response.data.data;
};

/**
 * Get summary of SNP counts by subcategory for a specific category
 */
export const getSubcategorySummary = async (
	category: string,
	minQuality: number = 1
): Promise<StagingSubcategorySummary[]> => {
	const response = await http.get(`${API_BASE_URL}/staging/summary/subcategories/${category}`, {
		params: { min_quality: minQuality }
	});

	return response.data.data;
};

/**
 * Get list of distinct genes with SNP counts
 */
export const getGeneList = async (minQuality: number = 1): Promise<GeneListItem[]> => {
	const response = await http.get(`${API_BASE_URL}/staging/genes`, {
		params: { min_quality: minQuality }
	});

	return response.data.data;
};

/**
 * Bulk import staging records
 */
export const bulkImportStaging = async (records: Partial<Staging>[]): Promise<BulkImportResult> => {
	const response = await http.post(
		`${API_BASE_URL}/staging/bulk-import`,
		{ records }
	);

	return response.data;
};

/**
 * Bulk delete staging records
 */
export const bulkDeleteStaging = async (ids: number[]): Promise<{ deleted_count: number }> => {
	const response = await http.post(
		`${API_BASE_URL}/staging/bulk-delete`,
		{ ids }
	);

	return response.data;
};